import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchVisionZones,
  saveVisionZones,
  InventoryApiError,
} from '../../services/inventoryApi';
import './VisionZoneConfig.css';

interface VisionZoneConfigProps {
  selectedStore: string;
  enterpriseId: string;
}

interface Zone {
  zoneId?: string;
  cameraId: string;
  name: string;
  polygon: number[][]; // array of [x,y]
  whitelistedIngredientIds: string[];
}

interface Camera {
  cameraId: string;
  name?: string;
}

interface ZoneResponse {
  storeId: string;
  cameras?: Camera[];
  zones?: Zone[];
}

const emptyZone = (cameraId: string): Zone => ({
  cameraId,
  name: '',
  polygon: [],
  whitelistedIngredientIds: [],
});

const VisionZoneConfig: React.FC<VisionZoneConfigProps> = ({ selectedStore }) => {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    setError(null);
    try {
      const res: ZoneResponse = await fetchVisionZones(selectedStore);
      setCameras(res?.cameras || []);
      setZones(res?.zones || []);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load vision zones');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    load();
  }, [load]);

  const addZone = (cameraId: string) => {
    setZones((z) => [...z, emptyZone(cameraId)]);
  };

  const updateZone = (idx: number, patch: Partial<Zone>) => {
    setZones((z) => z.map((zone, i) => (i === idx ? { ...zone, ...patch } : zone)));
  };

  const removeZone = (idx: number) => {
    setZones((z) => z.filter((_, i) => i !== idx));
  };

  const parsePolygon = (text: string): number[][] | null => {
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) return null;
      const ok = parsed.every(
        (p) => Array.isArray(p) && p.length === 2 && p.every((n) => typeof n === 'number')
      );
      return ok ? parsed : null;
    } catch {
      return null;
    }
  };

  const parseIngredients = (text: string): string[] =>
    text
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await saveVisionZones(selectedStore, zones);
      setSuccess('Zones saved');
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to save zones');
    } finally {
      setSaving(false);
    }
  };

  const zonesByCamera = cameras.map((c) => ({
    camera: c,
    list: zones
      .map((z, idx) => ({ z, idx }))
      .filter(({ z }) => z.cameraId === c.cameraId),
  }));

  return (
    <div className="vision-zones">
      <div className="vision-zones__header">
        <div>
          <h2>Vision Zone Config</h2>
          <p className="vision-zones__sub">
            Define camera zones and whitelisted ingredients for the vision count pipeline
          </p>
        </div>
        <button
          className="btn-modern btn-modern-accent"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Zones'}
        </button>
      </div>

      {error && <div className="vision-zones__error">{error}</div>}
      {success && <div className="vision-zones__success">{success}</div>}
      {loading && <div className="vision-zones__state">Loading...</div>}

      {!loading && cameras.length === 0 && (
        <div className="vision-zones__state">
          No cameras registered for this store yet. Register cameras via the UniFi network
          management tab first.
        </div>
      )}

      {!loading &&
        zonesByCamera.map(({ camera, list }) => (
          <div key={camera.cameraId} className="vision-zones__camera">
            <div className="vision-zones__camera-head">
              <h3>{camera.name || camera.cameraId}</h3>
              <button
                className="btn-modern btn-modern-secondary"
                onClick={() => addZone(camera.cameraId)}
              >
                Add Zone
              </button>
            </div>

            {list.length === 0 && (
              <div className="vision-zones__state vision-zones__state--small">
                No zones configured for this camera.
              </div>
            )}

            {list.map(({ z, idx }) => {
              const polyText = JSON.stringify(z.polygon);
              const ingText = z.whitelistedIngredientIds.join(', ');
              const polyValid = parsePolygon(polyText) !== null;
              return (
                <div key={idx} className="vision-zones__zone">
                  <div className="vision-zones__row">
                    <label>
                      Zone Name
                      <input
                        type="text"
                        value={z.name}
                        onChange={(e) => updateZone(idx, { name: e.target.value })}
                        placeholder="e.g., Walk-in shelf A"
                      />
                    </label>
                    <button
                      className="btn-modern btn-modern-secondary vision-zones__remove"
                      onClick={() => removeZone(idx)}
                    >
                      Remove
                    </button>
                  </div>
                  <label className="vision-zones__full">
                    Polygon (JSON array of [x, y] pairs)
                    <textarea
                      rows={3}
                      defaultValue={polyText}
                      onBlur={(e) => {
                        const parsed = parsePolygon(e.target.value);
                        if (parsed) updateZone(idx, { polygon: parsed });
                      }}
                      placeholder="[[0,0],[100,0],[100,100],[0,100]]"
                    />
                    {!polyValid && (
                      <small className="vision-zones__hint-bad">
                        Invalid polygon JSON
                      </small>
                    )}
                  </label>
                  <label className="vision-zones__full">
                    Whitelisted ingredient IDs (comma-separated)
                    <input
                      type="text"
                      defaultValue={ingText}
                      onBlur={(e) =>
                        updateZone(idx, {
                          whitelistedIngredientIds: parseIngredients(e.target.value),
                        })
                      }
                      placeholder="ing-tomato, ing-lettuce"
                    />
                  </label>
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
};

export default VisionZoneConfig;
